// infcode_change - new file
import fs from "fs/promises"
import path from "path"
import * as Log from "@opencode-ai/core/util/log"
import { TerminalRunSettingsStore } from "@/kilocode/config/terminal-run-settings-store"
import { addMigrationItem, createMigrationReport, type MigrationReport } from "./types"
import { errorMessage, exists, isRecord } from "./util"

const LOG_PREFIX = "[infcode:migration:agent]"
const log = Log.create({ service: LOG_PREFIX })

const LEGACY_DEFAULT_DANGEROUS_COMMANDS = ["rm", "kill", "chmod", "del", "erase", "rd", "rmdir", "Remove-Item"]
const TARGET_PATH = "terminal_run_settings/global"

export type AgentMigrationItem = {
  status: "migrated" | "rewritten" | "skipped" | "failed"
  sourcePath: string
  targetPath?: string
  reason?: string
  message?: string
}

export type AgentMigrationReport = MigrationReport<AgentMigrationItem>

export namespace AgentMigration {
  export type Item = AgentMigrationItem
  export type Report = AgentMigrationReport

  export async function run(input: { home: string }): Promise<AgentMigrationReport> {
    const report = createMigrationReport<AgentMigrationItem>()
    const sourcePath = path.join(input.home, ".infcode", "index", "globalContext.json")

    if (!(await exists(sourcePath))) {
      addMigrationItem(report, {
        status: "skipped",
        sourcePath,
        targetPath: TARGET_PATH,
        reason: "legacy-file-missing",
      })
      return report
    }

    const parsed = await readLegacyGlobalContext(sourcePath)
    if (!parsed.ok) {
      addMigrationItem(report, {
        status: "failed",
        sourcePath,
        targetPath: TARGET_PATH,
        reason: parsed.reason,
        message: parsed.message,
      })
      return report
    }

    const agent = legacyAgent(parsed.value)
    if (!agent) {
      addMigrationItem(report, {
        status: "skipped",
        sourcePath,
        targetPath: TARGET_PATH,
        reason: "legacy-agent-missing",
      })
      return report
    }

    try {
      const result = TerminalRunSettingsStore.saveMigrated({
        autoApprove: agent.enabled === true,
        askList: legacyDangerousCommands(agent.dangerousCommands),
      })
      if (result === "exists") {
        addMigrationItem(report, {
          status: "skipped",
          sourcePath,
          targetPath: TARGET_PATH,
          reason: "terminal-run-settings-exists",
        })
        return report
      }
      addMigrationItem(report, {
        status: result === "rewritten" ? "rewritten" : "migrated",
        sourcePath,
        targetPath: TARGET_PATH,
      })
    } catch (err) {
      const message = errorMessage(err)
      log.warn("failed to migrate v1 agent settings", { sourcePath, error: message })
      addMigrationItem(report, {
        status: "failed",
        sourcePath,
        targetPath: TARGET_PATH,
        reason: "write-failed",
        message,
      })
    }

    return report
  }
}

async function readLegacyGlobalContext(file: string): Promise<
  | {
      ok: true
      value: unknown
    }
  | {
      ok: false
      reason: "read-failed" | "invalid-json"
      message: string
    }
> {
  let text: string
  try {
    text = await fs.readFile(file, "utf8")
  } catch (err) {
    return { ok: false, reason: "read-failed", message: errorMessage(err) }
  }
  try {
    return { ok: true, value: JSON.parse(text) as unknown }
  } catch (err) {
    return { ok: false, reason: "invalid-json", message: errorMessage(err) }
  }
}

function legacyAgent(value: unknown): Record<string, unknown> | undefined {
  if (!isRecord(value)) return undefined
  if (!isRecord(value.sharedConfig)) return undefined
  return isRecord(value.sharedConfig.agent) ? value.sharedConfig.agent : undefined
}

function legacyDangerousCommands(value: unknown): string[] {
  if (!Array.isArray(value)) return LEGACY_DEFAULT_DANGEROUS_COMMANDS
  return value.filter((item): item is string => typeof item === "string")
}
